import React from 'react';
import { ChevronLeft, MapPin, Layers, User } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import StatusDot from '../ui/StatusDot';
import { RealQRCode } from '../../utils/qr';

export default function VisitorCardDetailView({ visitorCard, site, zone, zones = [], onBack, onSimulateScan, onUpdateSettings }) {
  const statusColors = { SAFE: C.green, NEED_HELP: C.red, NOT_RESPONDED: C.amber, EVACUATED: C.textSecondary };
  const statusColor = statusColors[visitorCard.emergencyStatus] || C.textMuted;

  const locationStr = visitorCard.currentZoneId
    ? [site?.name, zone?.name].filter(Boolean).join(' → ')
    : 'Not currently in a zone';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface, display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ChevronLeft size={16} /><span style={{ fontSize: '13px' }}>Back</span>
        </button>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary, flex: 1 }}>
          {visitorCard.visitorName || visitorCard.cardNumber}
        </h2>
        <span style={{ fontSize: '11px', background: C.blue + '22', color: C.blue, borderRadius: '6px', padding: '3px 8px', fontWeight: 600 }}>Visitor</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '24px', alignItems: 'start' }}>

          {/* Left: QR + status + actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '210px' }}>
            {/* QR badge */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Visitor Card QR</div>
              {visitorCard.qrHash ? (
                <>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '10px', display: 'inline-block', marginBottom: '10px' }}>
                    <RealQRCode value={visitorCard.qrHash} size={140} darkColor='#0a0e17' lightColor='#ffffff' />
                  </div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: C.textSecondary }}>{visitorCard.qrHash}</div>
                </>
              ) : (
                <div style={{ padding: '20px', color: C.textMuted, fontSize: '12px' }}>No QR assigned</div>
              )}
            </div>

            {/* Status + Zone Management */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <StatusDot status={visitorCard.emergencyStatus || 'NOT_RESPONDED'} size={12} />
                <span style={{ fontSize: '14px', fontWeight: 700, color: statusColor }}>
                  {(visitorCard.emergencyStatus || 'NOT_RESPONDED').replace(/_/g, ' ')}
                </span>
              </div>
              {visitorCard.emergencyType && (
                <div style={{ fontSize: '12px', color: EMERGENCY_META[visitorCard.emergencyType]?.color || C.amber, marginBottom: '10px', fontWeight: 600 }}>
                  {EMERGENCY_META[visitorCard.emergencyType]?.label || visitorCard.emergencyType}
                </div>
              )}
              <button onClick={() => onSimulateScan(visitorCard)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Layers size={12} />Zone Management
              </button>
            </div>
          </div>

          {/* Right: info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Identity */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Identity</div>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: C.surface, border: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={22} color={C.textMuted} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[
                  ['Card Number', visitorCard.cardNumber, true],
                  ['Visitor Company', visitorCard.visitorCompanyName || '—', false],
                  ['Site', site?.name || '—', false],
                ].map(([label, val, mono]) => (
                  <div key={label}>
                    <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontSize: '13px', color: C.textPrimary, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Allowed Zones */}
            {visitorCard.allowedZoneIds?.length > 0 && (
              <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allowed Zones</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {visitorCard.allowedZoneIds.map(zid => {
                    const name = zones.find(z => z.id === zid)?.name || zid;
                    return (
                      <span key={zid} style={{ fontSize: '11px', background: C.blue + '22', color: C.blue, borderRadius: '6px', padding: '3px 8px' }}>{name}</span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Current Location */}
            <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
              <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Location</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={14} color={visitorCard.currentZoneId ? C.blue : C.textMuted} />
                <span style={{ fontSize: '14px', fontWeight: 600, color: visitorCard.currentZoneId ? C.textPrimary : C.textMuted }}>{locationStr}</span>
              </div>
            </div>

            {/* Settings — admin-toggleable per-card flags */}
            {onUpdateSettings && (
              <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Settings</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Incident Reporting</div>
                    <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Show "Report Incident" when this visitor scans their card</div>
                  </div>
                  <div onClick={() => onUpdateSettings(visitorCard.id, { incidentReportingEnabled: !visitorCard.incidentReportingEnabled })}
                    style={{ width: '42px', height: '24px', borderRadius: '12px', background: visitorCard.incidentReportingEnabled ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{ position: 'absolute', top: '3px', left: visitorCard.incidentReportingEnabled ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
